import React, { useMemo, useState } from "react";

import CloseIcon from "@mui/icons-material/Close";
import { Autocomplete, Button, Chip, IconButton, Stack, TextField, Typography } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";
import { useDateFormatSelect } from "store";
import { DocumentMeeting } from "store/types";

interface Props {
  onCancel: () => void;
  onSubmit: (value: MeetingNoteData) => void;
  meeting?: DocumentMeeting;
  isEdit?: boolean;
}

interface MeetingNoteData {
  title: string;
  meeting_date: string;
  meeting_time_start: string;
  meeting_time_end: string;
  participants: string[];
}

export const CreateSectionManualContent: React.FC<Props> = ({ onCancel, onSubmit, meeting, isEdit }) => {
  const [inputValue, setInputValue] = useState("");
  const dateFormat = useDateFormatSelect();

  // convert iso time to local time
  const getLocalTime = (isoTime: string) => {
    if (!isoTime || typeof isoTime !== "string") return "";
    return dayjs.utc(isoTime, "HH:mm").local().format("HH:mm");
  };

  // convert local time to iso time
  const getUTCtime = (time: string, date: string) => {
    if (!time || !date) return "";

    const timeParts = time.split(":");
    if (timeParts.length !== 2) return "";

    const hours = parseInt(timeParts[0]);
    const minutes = parseInt(timeParts[1]);

    if (!isTimeValid(time)) {
      return "";
    }
    return dayjs(date).hour(hours).minute(minutes).utc().format("HH:mm");
  };

  const isTimeValid = (time: string) => {
    const timeParts = time.split(":");
    if (timeParts.length !== 2) return false;

    const hours = parseInt(timeParts[0]);
    const minutes = parseInt(timeParts[1]);

    return !isNaN(hours) && !isNaN(minutes) && hours >= 0 && hours <= 23 && minutes >= 0 && minutes <= 59;
  };

  const [displayDate, setDisplayDate] = useState<dayjs.Dayjs | null>(
    meeting?.meeting_date ? dayjs(meeting.meeting_date) : null
  );
  const [displayTimeStart, setDisplayTimeStart] = useState<string>(
    meeting?.meeting_time_start ? getLocalTime(meeting?.meeting_time_start) : ""
  );
  const [displayTimeEnd, setDisplayTimeEnd] = useState<string>(
    meeting?.meeting_time_end ? getLocalTime(meeting?.meeting_time_end) : ""
  );

  const [data, setData] = useState<MeetingNoteData>({
    title: meeting?.title || "",
    meeting_date: meeting?.meeting_date || "",
    meeting_time_start: meeting?.meeting_time_start || "",
    meeting_time_end: meeting?.meeting_time_end || "",
    participants: meeting?.participants || []
  });

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === "Enter" && inputValue.trim()) {
      event.preventDefault();
      setInputValue("");
      setData({
        ...data,
        participants: [...data.participants, inputValue.trim()]
      });
    }
  };

  const timeOptions = Array.from({ length: 96 }, (_, i) => {
    const hours = Math.floor(i / 4);
    const minutes = (i % 4) * 15;
    return `${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}`;
  });

  const isSubmitDisabled = useMemo(() => {
    const hasValidTimes =
      displayTimeStart && displayTimeEnd && isTimeValid(displayTimeStart) && isTimeValid(displayTimeEnd);

    return !data.title || !displayDate || !hasValidTimes || !data.participants.length;
  }, [data.title, displayDate, displayTimeStart, displayTimeEnd, data.participants.length]);

  const handleSubmit = () => {
    // convert local time to iso time before submit for backend
    const submitData = {
      ...data,
      meeting_date: data.meeting_date || "",
      meeting_time_start: displayTimeStart && data.meeting_date ? getUTCtime(displayTimeStart, data.meeting_date) : "",
      meeting_time_end: displayTimeEnd && data.meeting_date ? getUTCtime(displayTimeEnd, data.meeting_date) : "",
      participants: data.participants.filter((p) => p.trim())
    };
    onSubmit(submitData);
  };

  return (
    <Stack
      sx={{
        ...style,
        display: "flex",
        flexDirection: "column",
        width: 460,
        p: "32px",
        borderRadius: "8px",
        bgcolor: "white",
        gap: "24px",
        boxShadow:
          "0px 2px 4px -1px rgba(0, 0, 0, 0.20), 0px 4px 5px 0px rgba(0, 0, 0, 0.14), 0px 1px 10px 0px rgba(0, 0, 0, 0.12)"
      }}
    >
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ borderBottom: "1px solid #DAE0E2" }}
      >
        <Typography>{isEdit ? "Edit Meeting Note" : "Add Meeting Note"}</Typography>
        <IconButton onClick={onCancel}>
          <CloseIcon className="icon" />
        </IconButton>
      </Stack>

      <Stack gap="10px">
        <TextField
          label="Meeting Name"
          value={data.title}
          onChange={(e) => setData({ ...data, title: e.target.value })}
        />
        <Stack direction="row" gap="5px">
          <DatePicker
            value={displayDate}
            format={dateFormat}
            sx={{ minWidth: "200px" }}
            slotProps={{
              textField: {
                InputProps: {
                  readOnly: true
                },
                inputProps: {
                  readOnly: true
                }
              }
            }}
            onChange={(newValue) => {
              setDisplayDate(newValue);
              setData({
                ...data,
                meeting_date: newValue ? dayjs(newValue).format("YYYY-MM-DD") : ""
              });
            }}
          />
          <Autocomplete
            fullWidth
            options={timeOptions}
            value={displayTimeStart}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Start"
                placeholder="HH:MM"
                inputMode="none"
                InputProps={{
                  ...params.InputProps,
                  readOnly: true
                }}
                inputProps={{
                  ...params.inputProps,
                  readOnly: true
                }}
              />
            )}
            disableClearable
            popupIcon={null}
            forcePopupIcon={false}
            sx={{
              "& .MuiAutocomplete-endAdornment": {
                display: "none"
              }
            }}
            onChange={(_, newValue) => {
              setDisplayTimeStart(newValue || "");
              setData({ ...data, meeting_time_start: newValue || "" });
            }}
          />
          <Autocomplete
            fullWidth
            options={timeOptions}
            value={displayTimeEnd}
            renderInput={(params) => (
              <TextField
                {...params}
                label="End"
                placeholder="HH:MM"
                inputMode="none"
                InputProps={{
                  ...params.InputProps,
                  readOnly: true
                }}
                inputProps={{
                  ...params.inputProps,
                  readOnly: true
                }}
              />
            )}
            disableClearable
            popupIcon={null}
            forcePopupIcon={false}
            sx={{
              "& .MuiAutocomplete-endAdornment": {
                display: "none"
              }
            }}
            onChange={(_, newValue) => {
              setDisplayTimeEnd(newValue || "");
              setData({ ...data, meeting_time_end: newValue || "" });
            }}
          />
        </Stack>
        <Autocomplete
          multiple
          freeSolo
          value={data.participants}
          onChange={(_, newValue) => {
            setData({ ...data, participants: newValue });
          }}
          inputValue={inputValue}
          onInputChange={(_, newInputValue) => {
            setInputValue(newInputValue);
          }}
          options={[]}
          renderTags={(value: string[], getTagProps) =>
            value.map((option: string, index: number) => (
              <Chip
                sx={{ maxWidth: "150px", border: "none" }}
                variant="outlined"
                label={option}
                {...getTagProps({ index })}
                onDelete={() => {
                  const newParticipants = data.participants.filter((_, i) => i !== index);
                  setData({ ...data, participants: newParticipants });
                }}
                key={index}
              />
            ))
          }
          renderInput={(params) => <TextField {...params} label="Attendees" onKeyDown={handleKeyPress} />}
        />
      </Stack>
      <Stack direction="row" gap="10px" justifyContent="flex-end">
        <Button variant="outlined" sx={{ minWidth: "90px" }} onClick={onCancel}>
          Cancel
        </Button>
        <Button
          disabled={isSubmitDisabled}
          onClick={handleSubmit}
          variant="contained"
          sx={{ backgroundColor: "#071F35", minWidth: "70px" }}
        >
          {isEdit ? "Save" : "Add"}
        </Button>
      </Stack>
    </Stack>
  );
};

const style = {
  position: "absolute" as "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  borderRadius: "10px"
};
// how to use
// {async () => {
//   const data = await openPrompt({ Component: MeetingNoteContent });
//   console.log(data);
// }}
